import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import {
  LayoutDashboard, FileText, ShoppingCart, Users, Truck, Package, Wallet, Boxes, Contact as PayrollIcon, BookOpen, UserCog, ScrollText, Settings,
  CreditCard, ShieldCheck, ArrowLeftRight, CalendarCheck2, Landmark as AssetIcon, Landmark, Briefcase, FolderUp, Menu, Languages, Building2, Plus, AlertTriangle, Camera,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { Loading } from "@/components/kit";
import Onboarding from "@/pages/Onboarding";
import { Logo } from "@/components/Logo";
import { MfaGate } from "@/components/MfaSetup";
import { DemoBar, DemoTour } from "@/components/DemoTour";

const NAV = [
  { to: "/app", k: "dashboard", icon: LayoutDashboard, m: "dashboard", end: true },
  { to: "/app/sales", k: "sales", icon: FileText, m: "sales" },
  { to: "/app/purchases", k: "purchases", icon: ShoppingCart, m: "purchases" },
  { to: "/app/receipts", k: "receipts_queue", icon: Camera, m: "purchases" },
  { to: "/app/payments", k: "payments", icon: Wallet, m: "payments" },
  { to: "/app/bank", k: "bank_rec", icon: ArrowLeftRight, m: "bank" },
  { to: "/app/payroll", k: "payroll", icon: PayrollIcon, m: "payroll" },
  { to: "/app/clients", k: "clients", icon: Users, m: "partners" },
  { to: "/app/suppliers", k: "suppliers", icon: Truck, m: "partners" },
  { to: "/app/items", k: "items", icon: Package, m: "items" },
  { to: "/app/stock", k: "stock", icon: Boxes, m: "stock", plan: "stock" },
  { to: "/app/accounting", k: "accounting", icon: BookOpen, m: "accounting", plan: "accounting" },
  { to: "/app/assets", k: "assets", icon: AssetIcon, m: "accounting", plan: "accounting" },
  { to: "/app/closing", k: "closing", icon: CalendarCheck2, m: "accounting", plan: "accounting" },
  { to: "/app/declarations", k: "declarations", icon: Landmark, m: "declarations" },
  { to: "/app/justificatifs", k: "justificatifs", icon: FolderUp, m: "justificatifs" },
  { to: "/app/members", k: "members", icon: UserCog, m: "members" },
  { to: "/app/audit", k: "audit", icon: ScrollText, m: "audit" },
  { to: "/app/settings", k: "settings", icon: Settings, m: "settings" },
  { to: "/app/subscription", k: "subscription", icon: CreditCard, m: "subscription" },
];

export const LangToggle = ({ dark }) => {
  const { lang, setLang } = useApp();
  return (
    <button data-testid="lang-toggle" onClick={() => setLang(lang === "fr" ? "ar" : "fr")}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${dark ? "border-slate-700 text-slate-200 hover:bg-white/5" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
      <Languages className="h-3.5 w-3.5" />{lang === "fr" ? "العربية" : "Français"}
    </button>
  );
};

const Sidebar = ({ onNavigate }) => {
  const { t, can, company, me } = useApp();
  const items = NAV.filter((n) => can(n.m) && (!n.plan || company?.plan?.[n.plan]));
  return (
    <div className="flex h-full flex-col bg-[#111418] text-slate-300">
      <div className="px-5 py-5"><Logo dark size={34} /></div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
        {(me?.companies.length > 1 || me?.companies.some((c) => c.role === "firm")) && (
          <NavLink to="/app/firm" onClick={onNavigate} data-testid="nav-firm"
            className={({ isActive }) => `mb-3 flex items-center gap-3 rounded-lg border border-teal-700/40 px-3 py-2 text-sm ${isActive ? "bg-teal-600/20 text-white" : "text-teal-300 hover:bg-white/5"}`}>
            <Briefcase className="h-4 w-4" />{t("firm_space")}
          </NavLink>
        )}
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.k}`}
            className={({ isActive }) => `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-teal-600/15 text-white" : "hover:bg-white/5 hover:text-white"}`}>
            {({ isActive }) => (<><n.icon className={`h-4 w-4 ${isActive ? "text-teal-400" : "text-slate-500 group-hover:text-slate-300"}`} />{t(n.k)}</>)}
          </NavLink>
        ))}
        {me?.is_superadmin && (
          <NavLink to="/app/backoffice" onClick={onNavigate} data-testid="nav-backoffice"
            className={({ isActive }) => `mt-4 flex items-center gap-3 rounded-lg border border-orange-700/40 px-3 py-2 text-sm ${isActive ? "bg-orange-700/20 text-white" : "text-orange-300 hover:bg-white/5"}`}>
            <ShieldCheck className="h-4 w-4" />{t("backoffice")}
          </NavLink>
        )}
      </nav>
      <div className="border-t border-slate-800 px-5 py-3 text-[11px] text-slate-500">TEIF 1.8.7 · TTN <span className="text-orange-400">{t("simulated")}</span></div>
    </div>
  );
};

const CompanySwitcher = () => {
  const { me, company, setCompanyId, setCreatingNew, t } = useApp();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="company-switcher" className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-slate-300">
          <Building2 className="h-4 w-4 shrink-0 text-teal-700" />
          <span className="truncate font-medium max-w-[160px] sm:max-w-[260px]">{company?.name || "…"}</span>
          {company?.support && <span className="rounded bg-orange-100 px-1.5 text-[10px] font-bold text-orange-800">SUPPORT</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 bg-white">
        {me?.companies.map((c) => (
          <DropdownMenuItem key={c.id} data-testid={`company-option-${c.id}`} onClick={() => { setCompanyId(c.id); nav("/app"); }}>
            <span className="truncate">{c.name}</span><span className="ms-auto text-[10px] text-slate-400">{t(`r_${c.role}`)}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="company-new" onClick={() => setCreatingNew(true)}>
          <Plus className="h-4 w-4 me-2" />{t("new_company")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function AppShell() {
  const { me, company, companyId, t, readOnly, creatingNew, demo } = useApp();
  const demoMode = !!demo;
  const [open, setOpen] = useState(false);
  if (!me) return <Loading />;
  if (me.mfa?.enforced && !me.mfa.enrolled) return <MfaGate />;
  if (!companyId || creatingNew) return <Onboarding />;
  if (!company) return <Loading />;
  return (
    <div className={`min-h-screen lg:ps-64 ${demoMode ? "pt-7" : ""}`}>
      {demoMode && <DemoBar />}
      {demoMode && <DemoTour />}
      <aside className={`fixed bottom-0 start-0 z-30 hidden w-64 lg:block ${demoMode ? "top-7" : "top-0"}`}><Sidebar /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side={document.documentElement.dir === "rtl" ? "right" : "left"} className="w-64 border-0 p-0"><Sidebar onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <header className={`sticky ${demoMode ? "top-7" : "top-0"} z-20 flex items-center gap-3 border-b border-slate-200 bg-white/90 px-4 py-2.5 backdrop-blur-md sm:px-6`}>
        <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
        <CompanySwitcher />
        <span data-testid="sub-badge" className={`hidden sm:inline rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${company.sub_state === "active" ? "bg-emerald-50 text-emerald-700" : company.sub_state === "expired" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
          {company.plan?.name}{company.sub_state !== "trial" && ` · ${t(company.sub_state)}`}
        </span>
        <div className="ms-auto flex items-center gap-3"><LangToggle />{!demoMode && <UserButton />}</div>
      </header>
      {readOnly && (
        <div data-testid="readonly-banner" className="flex items-center gap-2 bg-red-600 px-6 py-2 text-sm text-white"><AlertTriangle className="h-4 w-4" />{t("read_only")}</div>
      )}
      <main className="mx-auto max-w-[1400px] space-y-6 p-4 sm:p-6 lg:p-8"><Outlet /></main>
    </div>
  );
}
